import {
	CircularProgress,
	Grid2,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	ChatBotBubbleIcon,
	ChatConsultant,
	DoubleTick,
	DownloadIcon,
	SingleTick,
} from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';

interface ChatDataProps {
	position: string;
	text: string;
	chatTime: string;
	fileName?: string;
	fileUrl?: string;
	onDownload?: (url: string) => void;
	caption?: string;
	chatOwner: string;
	isDownloading?: boolean;
	downloadingFile?: string;
	status: string | null;
}

function ChatBubble(data: ChatDataProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopRightRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		} else {
			return {
				borderTopLeftRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		}
	}

	function isMessageIsTooLong() {
		const textLength = data.text.length;
		if (textLength > 40) {
			return true;
		} else {
			return false;
		}
	}

	function getChatOwnerTag(type: string) {
		if (type === 'consultant') {
			return 'Consultant';
		} else {
			return 'AI Assistant';
		}
	}

	function getChatIcon(type: string) {
		if (type === 'consultant') {
			return <ChatConsultant />;
		} else if (type === 'ai') {
			return <ChatBotBubbleIcon />;
		} else {
			return <></>;
		}
	}

	function getChatStatusIcon(type: string | null) {
		if (type === 'delivered') {
			return <DoubleTick color="#aaaaaa" />;
		} else if (type === 'seen') {
			return <DoubleTick color="#34B7F1" />;
		} else {
			return <SingleTick color="#aaaaaa" />;
		}
	}

	return (
		<>
			<Grid2
				component={'section'}
				sx={{
					paddingX: '20px',
					paddingY: '10px',
					marginX: '5px',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor:
						data.position === 'left' ? '#F2F4F5' : MAIN_THEME_COLOR,
					marginTop: data.position === 'right' ? '15px' : '0px',
					width: !isMobile
						? isMessageIsTooLong()
							? '40%'
							: null
						: null,
					...getChatBubbleStyle(),
					minWidth: 0,
				}}>
				{data.position === 'right' && (
					<Typography
						variant="body2"
						textAlign={'left'}
						color={'#E2FAFB'}
						fontWeight={'700'}
						paddingY={'5px'}>
						{getChatOwnerTag(data.chatOwner)}
					</Typography>
				)}
				{data.fileName && data.fileUrl ? (
					<Grid2 container flexDirection={'column'}>
						<Grid2
							container
							flexDirection={'row'}
							width={'100%'}
							alignItems={'center'}
							sx={{
								backgroundColor:
									data.position === 'left'
										? '#F0F0F0'
										: MAIN_THEME_COLOR,
								padding: '5px',
								borderRadius: '8px',
								boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1)',
							}}>
							<Typography
								variant="body2"
								style={{
									overflowWrap: 'break-word',
									maxWidth: isMobile
										? 'calc(90% - 10px)'
										: 'calc(98% - 10px)',
								}}
								color={
									data.position === 'left'
										? '#000000'
										: '#FFFFFF'
								}>
								{data.fileName}
							</Typography>
							{data.isDownloading &&
							data.downloadingFile === data.fileUrl ? (
								<CircularProgress
									sx={{
										color:
											data.position === 'left'
												? '#28696D'
												: '#FFFFFF',
										marginLeft: '5px',
										padding: '2px',
									}}
									size={24}
								/>
							) : (
								<IconButton
									sx={{
										marginLeft: '5px',
										padding: '2px',
									}}
									onClick={() =>
										data.onDownload &&
										data.onDownload(
											data.fileUrl ? data.fileUrl : '',
										)
									}>
									<DownloadIcon
										color={
											data.position === 'left'
												? '#000000'
												: '#FFFFFF'
										}
									/>
								</IconButton>
							)}
						</Grid2>
						<Typography
							variant="caption"
							sx={{
								color:
									data.position === 'left'
										? '#8E8E8E'
										: '#B0B0B0',
								maxWidth: '100%',
								overflowWrap: 'break-word',
								paddingTop: '5px',
								paddingLeft: '5px',
							}}>
							{data.caption}
						</Typography>
					</Grid2>
				) : (
					<Typography
						variant="body2"
						style={{
							overflowWrap: 'break-word',
							whiteSpace: 'pre-line',
						}}
						color={
							data.position === 'left' ? '#000000' : '#FFFFFF'
						}>
						{data.text}
					</Typography>
				)}
				<Typography
					fontSize={'8px'}
					textAlign={data.position === 'left' ? 'start' : 'end'}
					color={data.position === 'left' ? '#000000' : '#FFFFFF'}
					paddingTop={'5px'}>
					{data.chatTime}
					&nbsp;&nbsp;
					{data.position === 'right' &&
						getChatStatusIcon(data.status)}
				</Typography>
			</Grid2>
			{data.position === 'right' && getChatIcon(data.chatOwner)}
		</>
	);
}

function ChatRow(chat: ChatDataProps) {
	return (
		<Grid2
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			padding={'10px'}>
			<ChatBubble
				position={chat.position}
				text={chat.text}
				chatTime={chat.chatTime}
				fileName={chat.fileName}
				fileUrl={chat.fileUrl}
				onDownload={chat.onDownload}
				caption={chat.caption}
				chatOwner={chat.chatOwner}
				isDownloading={chat.isDownloading}
				downloadingFile={chat.downloadingFile}
				status={chat.status}
			/>
		</Grid2>
	);
}

export default ChatRow;
